import React, { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Abstract signal/neural mesh for hero backdrop.
 * Pure SVG — animated with framer-motion for GPU-friendly opacity / stroke-dashoffset.
 * Deterministic (seeded) node positions so SSR/CSR stay stable.
 */
const seededRandom = (seed) => {
    let s = seed % 2147483647;
    if (s <= 0) s += 2147483646;
    return () => {
        s = (s * 16807) % 2147483647;
        return s / 2147483647;
    };
};

export const SignalMesh = ({ width = 1600, height = 900, nodes = 34, className = "" }) => {
    const data = useMemo(() => {
        const rand = seededRandom(7);
        const pts = Array.from({ length: nodes }, (_, i) => ({
            id: i,
            x: rand() * width,
            y: rand() * height,
            r: 0.8 + rand() * 1.6,
            phase: rand() * 3,
        }));
        // Build edges between nearest neighbours (max dist threshold)
        const threshold = Math.min(width, height) * 0.22;
        const edges = [];
        for (let i = 0; i < pts.length; i++) {
            for (let j = i + 1; j < pts.length; j++) {
                const dx = pts[i].x - pts[j].x;
                const dy = pts[i].y - pts[j].y;
                const d = Math.hypot(dx, dy);
                if (d < threshold) edges.push({ a: pts[i], b: pts[j], d });
            }
        }
        return { pts, edges };
    }, [width, height, nodes]);

    return (
        <svg
            viewBox={`0 0 ${width} ${height}`}
            preserveAspectRatio="xMidYMid slice"
            className={className}
            aria-hidden="true"
        >
            <defs>
                <linearGradient id="mesh-line" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#6E3CFF" stopOpacity="0.7" />
                    <stop offset="100%" stopColor="#00E5FF" stopOpacity="0.5" />
                </linearGradient>
                <radialGradient id="mesh-node" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
                    <stop offset="40%" stopColor="#8EE7FF" stopOpacity="0.9" />
                    <stop offset="100%" stopColor="#6E3CFF" stopOpacity="0" />
                </radialGradient>
                <filter id="mesh-blur">
                    <feGaussianBlur stdDeviation="0.6" />
                </filter>
            </defs>

            {/* Edges */}
            <g strokeWidth="0.6" stroke="url(#mesh-line)" filter="url(#mesh-blur)">
                {data.edges.map((e, i) => {
                    const opacity = Math.max(0.04, 0.35 - (e.d / 500));
                    return (
                        <motion.line
                            key={`e-${i}`}
                            x1={e.a.x}
                            y1={e.a.y}
                            x2={e.b.x}
                            y2={e.b.y}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: [opacity * 0.2, opacity, opacity * 0.2] }}
                            transition={{
                                duration: 7 + (i % 5),
                                repeat: Infinity,
                                ease: "easeInOut",
                                delay: (i % 12) * 0.2,
                            }}
                        />
                    );
                })}
            </g>

            {/* Nodes */}
            <g>
                {data.pts.map((p, i) => (
                    <motion.circle
                        key={`n-${i}`}
                        cx={p.x}
                        cy={p.y}
                        r={p.r * 3}
                        fill="url(#mesh-node)"
                        initial={{ opacity: 0.2 }}
                        animate={{ opacity: [0.25, 0.75, 0.25], scale: [0.9, 1.15, 0.9] }}
                        transition={{
                            duration: 5 + p.phase,
                            repeat: Infinity,
                            ease: "easeInOut",
                            delay: p.phase,
                        }}
                        style={{ transformOrigin: `${p.x}px ${p.y}px` }}
                    />
                ))}
            </g>

            {/* Sweeping signal line */}
            <motion.line
                x1="0"
                y1={height * 0.55}
                x2={width}
                y2={height * 0.45}
                stroke="#00E5FF"
                strokeWidth="0.7"
                strokeDasharray="3 6"
                initial={{ opacity: 0, pathLength: 0 }}
                animate={{ opacity: [0, 0.25, 0], pathLength: [0, 1, 1] }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            />
        </svg>
    );
};

export default SignalMesh;
